import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import API from "@/lib/api";
import { Badges } from "@/components/Badges";
import { ReviewDialog } from "@/components/ReviewDialog";
import { whenLabel, timeAgo, distanceLabel, money } from "@/lib/format";
import { ArrowLeft, MapPin, Clock, Languages, Share2, Flag, Star, Users, Check, Eye, UserPlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AdSlot } from "@/components/AdSlot";

const ASKED = { private: "Privato", company: "Azienda", entity: "Ente" };

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getCategory, catName } = useApp();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [avails, setAvails] = useState([]);
  const [refStats, setRefStats] = useState(null);
  const [msg, setMsg] = useState("");
  const [when, setWhen] = useState("da_concordare");
  const [price, setPrice] = useState("");
  const [openAvail, setOpenAvail] = useState(false);

  const load = () => {
    API.get(`/requests/${id}`).then((r) => {
      setReq(r.data);
      if (r.data.is_owner) {
        API.get(`/requests/${id}/availabilities`).then((a) => setAvails(a.data)).catch(() => {});
        API.get(`/requests/${id}/referral-stats`).then((s) => setRefStats(s.data)).catch(() => {});
      }
    }).catch(() => {});
  };
  useEffect(load, [id]);

  if (!req) return <Layout><p className="text-[#4A4E68]">Caricamento...</p></Layout>;
  const cat = getCategory(req.category);
  const sub = cat?.subcategories?.find((s) => s.slug === req.subcategory);
  const subLabel = req.other_description || (sub ? catName(sub) : "");
  const noPrice = cat?.no_price;

  const submitAvail = async () => {
    try {
      let referral_token = null;
      try {
        const ref = JSON.parse(sessionStorage.getItem("fydate_ref") || "null");
        if (ref && ref.request_id === id) referral_token = ref.token;
      } catch {}
      const res = await API.post(`/requests/${id}/availabilities`, {
        message: msg, when, price: price ? parseFloat(price) : null, referral_token,
      });
      if (referral_token) sessionStorage.removeItem("fydate_ref");
      toast.success("Disponibilità inviata!");
      setOpenAvail(false);
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const shareReferral = async () => {
    try {
      const res = await API.post(`/requests/${id}/referral`);
      const link = `${window.location.origin}/r/${res.data.token}`;
      const text = `Ciao! Su FYDATE qualcuno cerca: "${req.title}". Puoi aiutare? ${link}`;
      try { await navigator.clipboard.writeText(link); } catch {}
      toast.success("Link copiato. Si apre WhatsApp per condividerlo.");
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    } catch {
      toast.error("Impossibile creare il link. Riprova.");
    }
  };

  const doShareContact = async (aid) => {
    await API.post(`/availabilities/${aid}/share-contact`);
    toast.success("Contatti condivisi");
    load();
  };
  const doResolve = async (chosenId) => {
    await API.post(`/requests/${id}/resolve`, { chosen_id: chosenId });
    toast.success("Richiesta segnata come risolta");
    load();
  };
  const doReport = async () => {
    await API.post("/reports", { target_type: "request", target_id: id, reason: "Segnalazione utente" });
    toast.success("Segnalazione inviata");
  };

  return (
    <Layout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-[#4A4E68] mb-3 text-sm"><ArrowLeft size={16} /> Indietro</button>

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 fade-in">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {req.when === "today" && <span className="rounded-full bg-[#E8590C] text-white px-2.5 py-0.5 text-xs font-semibold">Oggi</span>}
          <span className="text-xs text-[#4A4E68] uppercase tracking-wide">{ASKED[req.asked_as]}</span>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: cat?.tint.iconBg, color: cat?.tint.text }}>{cat ? catName(cat) : ""}</span>
        </div>
        <h1 className="font-serif text-2xl font-bold text-[#0F1230]">{req.title}</h1>
        {subLabel && <p className="text-[#141A3A] font-medium mt-0.5">{subLabel}</p>}

        <div className="flex items-center gap-4 mt-3 text-sm text-[#4A4E68]">
          <span className="inline-flex items-center gap-1"><MapPin size={14} /> {req.is_online ? "Online" : `${req.city}${req.distance_km != null ? " · " + distanceLabel(req.distance_km) : ""}`}</span>
          <span className="inline-flex items-center gap-1"><Clock size={14} /> {timeAgo(req.created_at)}</span>
        </div>

        {req.description && <p className="text-[#2A2E48] mt-4 leading-relaxed">{req.description}</p>}
        {req.language !== "it" && (
          <button data-testid="translate-button" onClick={() => toast.info("Traduzione non ancora disponibile")} className="hidden"><Languages size={15} /> Traduci</button>
        )}
        {req.budget != null && <p className="mt-3 text-sm"><span className="text-[#4A4E68]">Budget indicativo:</span> <span className="font-semibold text-[#0F1230]">{money(req.budget)}</span></p>}

        <div className="mt-4 flex items-center gap-2 text-sm text-[#4A4E68]">
          <Users size={15} /> Già disponibili {req.availability_count} su {req.max_availabilities}
        </div>
        <div className="h-2 bg-[#EDEDF4] rounded-full mt-1 overflow-hidden">
          <div className="h-full bg-[#1F4FE0]" style={{ width: `${Math.min(100, (req.availability_count / req.max_availabilities) * 100)}%` }} />
        </div>

        {!req.is_owner && (
          <div className="mt-5 space-y-2">
            {!user ? (
              <Button data-testid="login-to-help" onClick={() => navigate("/register")} className="w-full h-12 bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] text-base rounded-full">Iscriviti per renderti disponibile</Button>
            ) : req.already_available ? (
              <div data-testid="you-are-available" className="inline-flex items-center gap-1 text-[#2E7D32] font-medium"><Check size={16} /> Sei disponibile</div>
            ) : (
              <Dialog open={openAvail} onOpenChange={setOpenAvail}>
                <DialogTrigger asChild>
                  <Button data-testid="make-available-button" className="w-full h-12 bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] text-base rounded-full">Mi rendo disponibile</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-serif">Mi rendo disponibile</DialogTitle>
                    <DialogDescription>Presentati al richiedente e indica quando puoi.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3">
                    <Textarea data-testid="avail-message" placeholder="Un messaggio breve per presentarti..." value={msg} onChange={(e) => setMsg(e.target.value)} />
                    <div>
                      <p className="text-sm text-[#4A4E68] mb-1">Quando puoi</p>
                      <div className="flex flex-wrap gap-2">
                        {["oggi", "domani", "in_settimana", "da_concordare"].map((w) => (
                          <button key={w} onClick={() => setWhen(w)} className={`px-3 h-9 rounded-full text-sm border ${when === w ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{whenLabel(w)}</button>
                        ))}
                      </div>
                    </div>
                    {!noPrice && <Input data-testid="avail-price" type="number" placeholder="Prezzo/tariffa indicativa (facoltativo)" value={price} onChange={(e) => setPrice(e.target.value)} />}
                  </div>
                  <DialogFooter>
                    <Button data-testid="avail-submit" onClick={submitAvail} disabled={!msg} className="bg-[#1F4FE0] text-white rounded-full">Invia disponibilità</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {user && <Button data-testid="referral-button" onClick={shareReferral} variant="outline" className="w-full h-11 rounded-full border-[#1F4FE0] text-[#141A3A]"><Share2 size={16} className="mr-1" /> Conosco chi può farlo</Button>}
            <p className="text-xs text-[#4A4E68] text-center">Il telefono di chi chiede resta nascosto finché non ti sceglie.</p>
            {user && <button data-testid="report-button" onClick={doReport} className="text-xs text-[#4A4E68] inline-flex items-center gap-1 mx-auto"><Flag size={12} /> Segnala</button>}
          </div>
        )}
      </div>

      {req.is_owner && (
        <div className="mt-5">
          <div className="flex justify-end mb-3">
            <button data-testid="delete-request-button" onClick={async () => {
              if (!window.confirm("Vuoi eliminare questa richiesta? Non sarà più visibile a nessuno.")) return;
              try { await API.delete(`/requests/${id}`); toast.success("Richiesta eliminata"); navigate("/my-requests"); }
              catch { toast.error("Impossibile eliminare la richiesta"); }
            }} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#C0262D] bg-white rounded-full px-4 h-10 shadow-[0_2px_10px_-4px_rgba(15,18,48,0.15)] hover:bg-[#FDECEC] transition-colors">
              <Trash2 size={16} /> Elimina richiesta
            </button>
          </div>
          {refStats && refStats.shares > 0 && (
            <div data-testid="referral-stats" className="bg-white rounded-[18px] border border-[#DADFEA] p-4 mb-4">
              <h3 className="font-semibold text-[#0F1230] mb-2 inline-flex items-center gap-1"><Share2 size={16} className="text-[#141A3A]" /> Il tuo passaparola</h3>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-[#EEF1F7] rounded-xl p-3 text-center">
                  <div className="text-xl font-serif font-bold text-[#141A3A]" data-testid="refstat-clicks">{refStats.clicks}</div>
                  <div className="text-xs text-[#4A4E68] inline-flex items-center gap-1"><Eye size={12} /> aperture</div>
                </div>
                <div className="bg-[#EEF1F7] rounded-xl p-3 text-center">
                  <div className="text-xl font-serif font-bold text-[#141A3A]" data-testid="refstat-visitors">{refStats.visitors}</div>
                  <div className="text-xs text-[#4A4E68] inline-flex items-center gap-1"><Users size={12} /> persone</div>
                </div>
                <div className="bg-[#EEF1F7] rounded-xl p-3 text-center">
                  <div className="text-xl font-serif font-bold text-[#E8590C]" data-testid="refstat-conversions">{refStats.conversions}</div>
                  <div className="text-xs text-[#4A4E68] inline-flex items-center gap-1"><UserPlus size={12} /> disponibili</div>
                </div>
              </div>
            </div>
          )}
          <h2 className="font-serif text-xl font-semibold text-[#0F1230] mb-3">Disponibilità ricevute</h2>
          {avails.length === 0 ? (
            <p className="text-[#4A4E68]">Ancora nessuna disponibilità.</p>
          ) : (
            <div className="space-y-3">
              {avails.map((a) => (
                <div key={a.id} data-testid="availability-received" className="bg-white rounded-[18px] border border-[#DADFEA] p-4">
                  <div className="flex items-center gap-2 justify-between">
                    <button onClick={() => navigate(`/u/${a.responder_id}`)} className="font-semibold text-[#0F1230]">{a.responder_name}</button>
                    {a.profile?.avg_rating != null && <span className="text-sm text-[#4A4E68] inline-flex items-center gap-0.5"><Star size={13} className="fill-[#E8590C] text-[#E8590C]" /> {a.profile.avg_rating}</span>}
                  </div>
                  <Badges badges={a.profile?.badges || []} />
                  <p className="text-[#2A2E48] mt-2 text-sm">{a.message}</p>
                  <div className="text-xs text-[#4A4E68] mt-1">Quando: {whenLabel(a.when)}{a.price ? ` · ${money(a.price)}` : ""}</div>
                  <div className="flex gap-2 mt-3">
                    <Button data-testid={`chat-${a.id}`} onClick={() => navigate(`/messages/${a.conversation_id}`)} variant="outline" size="sm" className="rounded-full">Chat</Button>
                    {a.contact_shared ? (
                      <span className="text-sm text-[#2E7D32] inline-flex items-center gap-1"><Check size={14} /> Contatti condivisi</span>
                    ) : (
                      <Button data-testid={`share-contact-${a.id}`} onClick={() => doShareContact(a.id)} size="sm" className="bg-[#1F4FE0] text-white rounded-full">Condividi i miei contatti</Button>
                    )}
                    {req.status !== "resolved" && <Button data-testid={`choose-${a.id}`} onClick={() => doResolve(a.responder_id)} size="sm" variant="outline" className="rounded-full ml-auto">Ho scelto lui/lei</Button>}
                  </div>
                  {req.status === "resolved" && req.chosen_id === a.responder_id && (
                    <div className="mt-3 pt-3 border-t border-[#F1F1F7] flex items-center gap-2">
                      <span className="text-sm text-[#2E7D32] inline-flex items-center gap-1"><Check size={14} /> Scelto</span>
                      {req.reviewed ? (
                        <span className="text-sm text-[#4A4E68] inline-flex items-center gap-1 ml-auto"><Star size={13} className="fill-[#E8590C] text-[#E8590C]" /> Recensione lasciata</span>
                      ) : (
                        <div className="ml-auto">
                          <ReviewDialog rid={id} revieweeId={a.responder_id} revieweeName={a.responder_name} onDone={load} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <AdSlot placement="detail" category={req.category} className="mt-40" />
    </Layout>
  );
}
